import { Component, computed, effect, inject, model, signal, ViewChild } from '@angular/core';
import { CommonModule } from "@angular/common";
import { MarkdownModule } from "ngx-markdown";
import { AngularSplitModule } from "angular-split";
import { ShowcaseOverviewComponent } from "./showcase-overview/showcase-overview.component";
import { ShowcaseApiComponent } from "./showcase-api/showcase-api.component";
import { ShowcaseSampleComponent } from "./showcase-sample/showcase-sample.component";
import { TabsModule } from "primeng/tabs";

// EZ
import { EzTabGroupComponent } from "@template-ui/components/ez-tab-group/ez-tab-group.component";
import { EzBaseComponent } from "@template-ui/components/ez-base.component";
import { DocMeta } from "@template-ui/decorators";
import { TabConfig } from "@template-ui/types";
import { LibraryDocService } from "./library-doc.service";
import { DocClassEntries, DocClassEntry } from "@template-ui/types/component-docs.model";
import { APP_SHOWCASE_SAMPLE_COMPONENTS_TOKEN } from "@template-ui/tokens";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { SidebarItem } from "@template-ui/components/sidebar/sidebar.types";

/**
 * @internal
 * The showcase component is used to display the EZ Library components, services etc.
 */
@Component({
    selector: 'ui-lib-showcase, ez-lib-showcase',
    imports: [
        CommonModule,
        MarkdownModule,
        AngularSplitModule,
        UiLibraryModule,
        TabsModule
    ],
    templateUrl: './ez-lib-showcase.component.html',
    styleUrl: './ez-lib-showcase.component.scss'
})
@DocMeta({})
export class EzLibShowcaseComponent extends EzBaseComponent<TabConfig> {
  private readonly navigationEndSig = this.createNavigationEndSignal();

  private docService = inject(LibraryDocService);


  @ViewChild('tabs') tabs?: EzTabGroupComponent

  basePath = 'libraryShowcase';

  ezLibBaseDocPath = "assets/template-ui/docs";

  static tabIndex = signal(0);

  tabValue = model<string | number>(0);

  staticTabIndex = EzLibShowcaseComponent.tabIndex;

  private appShowcaseSampleComponents = inject(APP_SHOWCASE_SAMPLE_COMPONENTS_TOKEN);

  // get staticTabIndex() { return EzLibShowcaseComponent.tabIndex()}
  // set staticTabIndex(value: number) {EzLibShowcaseComponent.tabIndex.set(value)}

  ezLibDocs?: DocClassEntries;
  // bitsLibDocs?: DocClassEntries;

  docLibraries = new Map<string, DocClassEntries>([]);

  selectedClassEntry = signal<DocClassEntry | undefined>(undefined);

  /**
   * The full path of the current document, passed to the src property of the markdown component
   */
  docPath = signal<string>("");

  tabConfig: TabConfig[] = [
    {
      label: "Overview",
      fragment: "overview",
      routeSegment: "overview",
      component: ShowcaseOverviewComponent,
      inputs: {
        "selectedDoc": this.selectedClassEntry
      }
    } as unknown as TabConfig,
    {
      label: "API",
      fragment: "api",
      routeSegment: "api",
      component: ShowcaseApiComponent,
      inputs: {
        "selectedDoc": this.selectedClassEntry
      }
    }  as unknown as TabConfig,
    {
      label: "Examples",
      fragment: "examples",
      routeSegment: "examples",
      component: ShowcaseSampleComponent,
      inputs: {
        "selectedDoc": this.selectedClassEntry
      },
      //hidden: computed(() => false)
      hidden: computed(() => {
        const doc = this.selectedClassEntry();
        return !doc?.showcaseComponentNames || doc?.showcaseComponentNames.length < 1
      })
    } as unknown as TabConfig,
  ];

  // NOTE: casing must match actual filenames when running on Angular Dev server
  private readonly baseSidebarConfig: SidebarItem[] = [
    {
      label: "Overview",
      itemClass: "divider",
      route: `${this.basePath}/docs/overview`,
    },

    {
      label: "Laws",
      itemClass: "divider",
      route: `${this.basePath}/docs/laws`,
    },

    {
      label: "Copilot Instructions",
      itemClass: "divider",
      route: `${this.basePath}/docs/copilot-instructions`,
    }
  ];

  sidebarConfig: SidebarItem[] = [...this.baseSidebarConfig];

  docsArr: DocClassEntry[] = [];

  // getComponentFromString(componentName: string): Type<unknown> | undefined {
  //   const allSampleComponents = new Map<string, Type<unknown>>([
  //     ...EZ_LIB_SHOWCASE_SAMPLE_COMPONENTS,
  //     ...this.appShowcaseSampleComponents,
  //   ]);
  //   return allSampleComponents.get(componentName);
  // }

  constructor() {
    super();
    this.thisClassName = 'EzLibShowcaseComponent';

    this.staticTabIndex.set(EzLibShowcaseComponent.tabIndex());

    // Change class based on route
    effect(() => {

      const navEnd = this.navigationEndSig();
      if (navEnd) {
        requestAnimationFrame(() => {

          const routeWithoutFragment = navEnd.urlAfterRedirects.split("#")[0];
          const routeWithoutQuery = routeWithoutFragment.split("?")[0];
          const urlParts = routeWithoutQuery.split('/').filter(Boolean);
          const docsIndex = urlParts.indexOf('docs');

          // If /docs/ is in the path, then set the docPath so the markdown component can get the file and render it
          if (docsIndex !== -1) {
            this.docPath.set(`${this.ezLibBaseDocPath}/${urlParts.slice(docsIndex + 1).join('/')}.md`);
            this.dlog('docPath:', this.docPath());
          }

          this.dlog('navEnd urlAfterRedirects:', navEnd.urlAfterRedirects);
          this.selectFromCurrentRoute();

          // Go to first tab if the new tab doesn't have Examples
          if(this.tabs)
          {
            const doc = this.selectedClassEntry();
            if( this.tabs.activeTabIndex == 2 && (!doc?.showcaseComponentNames || doc?.showcaseComponentNames.length < 1))
            {
              this.tabs.onTabChange(0);
            } else {
              this.syncActiveTabUrlState();
            }
          }
        });
      }
    });

    // Run when the docService has retrieved the library docs from the json file
    effect(() => {
      const libDocs = this.docService.libDocsSig();

      if(libDocs)
      {
        const libNames = Object.keys(libDocs);

        const allLibraryItems = [...this.baseSidebarConfig];

        // Just one for now, template-ui a.k.a. Base Library
        libNames.forEach((libName: string) => {
          const docs = libDocs[libName];
          const visibleDocs = Object.fromEntries( Object.entries(docs).filter(([_, value]) => !value.docMeta?.hide));
          const item = this.getLibItem(libName, visibleDocs || []);
          allLibraryItems.push(item);
        });

        this.sidebarConfig = allLibraryItems;
        this.selectFromCurrentRoute();
      }
    });

    // Run when the docService has retrieved the code docs from the json file
    effect(() => {
      const codeDocs = this.docService.codeDocsSig();
    });

    // Keep route-based selection hydrated whenever either docs or navigation state changes.
    // This prevents first-load blank states caused by race timing between route activation and docs loading.
    effect(() => {
      const libDocs = this.docService.libDocsSig();
      this.navigationEndSig();

      if (!libDocs) {
        return;
      }

      this.selectFromCurrentRoute();
    });
  }

  /**
   * Get a single SidebarItem that represents one library.
   * It will have a bunch of children categorized by class type (component, service etc.)
   * @param libName
   */
  getLibItem(libName: string, docs: DocClassEntries): SidebarItem {
    const libRouteSegment = this.toLibRouteSegment(libName);
    const rtn: SidebarItem = {
      label: libName,
      itemClass: 'divider-above'
    }

    // Get all the classname of the library
    //let classNames: string[] = libName == "EZ Library" ? this.docService.getAllEzLibClassNames() : this.docService.getAllBitsLibClassNames();
    // getAllLibClassNames
    //const classNames: string[] = libName == "EZ Library" ? this.docService.getAllLibClassNames('ezLib') : this.docService.getAllLibClassNames('bitsLib');
    const classNames: string[] = this.docService.getAllLibClassNames('ezLib');

    // Get all the docs for the items. These will be used to get stuff
    const docClassEntries: DocClassEntries = docs; //this.docService.getAllEzLibDocs() || [];

    // To hold all the categories of the libary
    const categories: string[] = [];

    // This will hold each child item
    const libraryItems: SidebarItem[] = [];

    let i = 0;

    const docClassEntryArr: DocClassEntry[] = [];

    // Convert all the docs to a flat list of items
    classNames.forEach((className: string) => {
      if (docClassEntries && docClassEntries[className]) {
        const docEntry = docClassEntries[className];
        docClassEntryArr.push(docEntry);

        // If the category is new, add it to the list
        if (docEntry.category && !categories.includes(docEntry.category)) {
          categories.push(docEntry.category);
        }
      }
    });

    // Now we have a flat list of all the documents and a list of the categories.
    // Loop through the categories and add

    // sort() puts WIP at the bottom and components at the top
    categories.sort().forEach((cat: string) => {

      const catItem: SidebarItem = {
        label: cat,
        isSectionHeader: false,
        order: i++,
        itemClass: "category",
        children: []
      };

      // Add categorized entries
      docClassEntryArr.filter(e => e.category == cat).forEach((doc: DocClassEntry) => {

        catItem.children!.push(
          {
            label: doc.title || doc.className || '',
            //fragment: doc.className,
            route: `${this.basePath}/${libRouteSegment}/${doc.className}`,
            order: i++
          }
        );
      });

      libraryItems.push(catItem);
    });

    // Add uncategorized entries
    this.docsArr.filter(e => !e.category).forEach((doc: DocClassEntry) => {

      libraryItems.push(
        {
          label: doc.title || doc.className || '',
          //fragment: doc.className,
          route: `${this.basePath}/${doc.className}`,
          order: i++
        }
      );
    });

    rtn.children = libraryItems;

    return rtn;
  }


  select(libName: string, className: string): void {
    const normalizedLibName = this.normalizeRouteSegment(libName);
    const resolvedLibName = this.resolveLibraryName(normalizedLibName);
    const normalizedClassName = this.normalizeRouteSegment(className);
    this.dlog('select() called', { libName, normalizedLibName, resolvedLibName, className, normalizedClassName });

    if(normalizedClassName == 'docs')
    {
      this.dlog('select() docs route detected, clearing selectedClassEntry');
      this.selectedClassEntry.set(undefined);
    }

    const doc = this.docService.getDocFor(resolvedLibName, normalizedClassName);
    this.dlog('select() getDocFor result', { found: !!doc });
    if(doc)
    {
      this.selectedClassEntry.set(doc);
      this.docPath.set("");
      this.dlog('select() success', { selectedClassName: doc.className, selectedLib: doc.libName });
    }
    else
    {
      this.dlog('select() no doc found', { libName, normalizedLibName, resolvedLibName, className, normalizedClassName });
    }

  }

  /** Re-applies current URL selection after docs are available to avoid blank first-load states. */
  private selectFromCurrentRoute(): void {
    const routeWithoutFragment = this.router.url.split("#")[0];
    const routeWithoutQuery = routeWithoutFragment.split("?")[0];
    const paths = routeWithoutQuery.split("/").filter(Boolean);
    const activeTabSegment = paths[paths.length - 1];
    const tabSegments = new Set(
      this.tabConfig
        .map((tab) => tab.routeSegment ?? tab.fragment)
        .filter((segment): segment is string => !!segment)
    );
    if (activeTabSegment && tabSegments.has(activeTabSegment)) {
      paths.pop();
    }

    const className = this.normalizeRouteSegment(paths.pop());
    const libName = this.normalizeRouteSegment(paths.pop());
    this.dlog('selectFromCurrentRoute() parsed route', {
      routerUrl: this.router.url,
      routeWithoutFragment,
      routeWithoutQuery,
      activeTabSegment,
      libName,
      className
    });

    if (!className || !libName) {
      this.dlog('selectFromCurrentRoute() missing libName/className, skipping selection');
      return;
    }

    this.select(libName, className);
  }

  /** Keeps URL tab state aligned with active showcase tab (supports both fragment and route-segment modes). */
  private syncActiveTabUrlState(): void {
    if (!this.tabs) {
      return;
    }

    const visibleTabs = this.tabs.visibleTabs();
    if (!visibleTabs.length) {
      return;
    }

    const currentFragment = this.activatedRoute.snapshot.fragment;
    const hasMatchingFragment = !!currentFragment && visibleTabs.some((tab) => tab.fragment === currentFragment);

    const routeWithoutFragment = this.router.url.split("#")[0];
    const routeWithoutQuery = routeWithoutFragment.split("?")[0];
    const routeSegments = routeWithoutQuery.split("/").filter(Boolean);
    const currentTabSegment = routeSegments[routeSegments.length - 1];
    const hasMatchingRouteSegment =
      !!currentTabSegment && visibleTabs.some((tab) => (tab.routeSegment ?? tab.fragment) === currentTabSegment);

    this.dlog('syncActiveTabUrlState()', {
      currentFragment,
      currentTabSegment,
      hasMatchingFragment,
      hasMatchingRouteSegment,
      activeTabIndex: this.tabs.activeTabIndex,
      visibleTabKeys: visibleTabs.map((tab) => tab.routeSegment ?? tab.fragment ?? this.read(tab.label))
    });

    if (hasMatchingFragment || hasMatchingRouteSegment) {
      return;
    }

    const safeIndex = Math.min(this.tabs.activeTabIndex, visibleTabs.length - 1);
    this.tabs.onTabChange(Math.max(0, safeIndex));
  }

  /** Decodes route/query values repeatedly to handle double-encoded URLs like `%2520`. */
  private normalizeRouteSegment(value: string | undefined): string {
    if (!value) {
      return '';
    }

    let decoded = value;
    for (let i = 0; i < 3; i++) {
      const next = decodeURIComponent(decoded);
      if (next === decoded) {
        break;
      }
      decoded = next;
    }

    return decoded;
  }

  /** Converts a library display name into a URL-safe slug used for showcase routes. */
  private toLibRouteSegment(libName: string): string {
    return libName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /** Resolves a route library segment (slug/raw) back to the configured library display name. */
  private resolveLibraryName(routeLibSegment: string): string {
    const docs = this.docService.libDocsSig();
    if (!docs) {
      return routeLibSegment;
    }

    const candidates = Object.keys(docs);
    if (candidates.includes(routeLibSegment)) {
      return routeLibSegment;
    }

    return candidates.find((name) => this.toLibRouteSegment(name) === routeLibSegment) ?? routeLibSegment;
  }
}
